import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { prefersReducedMotion } from '@/lib/animation-config'

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger)

export interface ParallaxOptions {
  speed?: number // -1 to 1, negative = reverse direction
  direction?: 'vertical' | 'horizontal'
  start?: string
  end?: string
}

/**
 * Apply parallax effect to elements on scroll
 *
 * @example
 * function HeroSection() {
 *   const bgRef = useParallax<HTMLDivElement>({ speed: 0.3 });
 *   return (
 *     <section className="relative">
 *       <div ref={bgRef} className="absolute inset-0">
 *         <img src="/bg.jpg" className="w-full h-full object-cover" />
 *       </div>
 *       <div className="relative z-10">Content</div>
 *     </section>
 *   );
 * }
 */
export function useParallax<T extends HTMLElement>(options: ParallaxOptions = {}) {
  const elementRef = useRef<T>(null)

  useEffect(() => {
    if (!elementRef.current) return

    // Skip for reduced motion preference
    if (prefersReducedMotion()) {
      return
    }

    const { speed = 0.5, direction = 'vertical', start = 'top bottom', end = 'bottom top' } = options

    // Calculate movement amount based on viewport
    const movement = speed * 100

    const animation = gsap.to(elementRef.current, {
      [direction === 'vertical' ? 'y' : 'x']: movement,
      ease: 'none',
      scrollTrigger: {
        trigger: elementRef.current,
        start,
        end,
        scrub: true,
      },
    })

    return () => {
      animation.scrollTrigger?.kill()
      animation.kill()
    }
  }, [options])

  return elementRef
}

/**
 * Apply parallax to multiple elements with the same selector
 */
export function useParallaxGroup(selector: string, options: ParallaxOptions = {}) {
  const containerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Skip for reduced motion preference
    if (prefersReducedMotion()) {
      return
    }

    const elements = containerRef.current.querySelectorAll(selector)
    if (elements.length === 0) return

    const { speed = 0.5, direction = 'vertical', start = 'top bottom', end = 'bottom top' } = options

    const movement = speed * 100
    const animations: gsap.core.Tween[] = []

    elements.forEach((el) => {
      const animation = gsap.to(el, {
        [direction === 'vertical' ? 'y' : 'x']: movement,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start,
          end,
          scrub: true,
        },
      })
      animations.push(animation)
    })

    return () => {
      animations.forEach((anim) => {
        anim.scrollTrigger?.kill()
        anim.kill()
      })
    }
  }, [selector, options])

  return containerRef
}
